import { PdpBreadcrumbs } from "@/components"
import { Pdp } from "@/components/Pdp"
import Layout from "@/layout"
import { NotFoundProps, PageNotFound } from "@/lib/notFound"
import { buildPageProps } from "@/lib/ssg/slug"
import { createClient } from "@jog/ct"
import { PdpProvider, usePdpBreadcrumb } from "@jog/react-components"
import {
    PrismicPdpJson,
    RedisProductItem,
    buildProductDetailInfo,
    fetchS3BreadcrumbSpecialCategory,
    fetchS3Pdp,
    fetchS3PdpSizes,
    prismicClient,
    retry,
    transBreadcrumbSpecialCategory,
    transPdp,
    transPdpSizes,
    typedBoolean,
} from "@jog/shared"
import { ProductPage, StaticPropsCommon } from "@jog/types"
import { GetStaticPropsContext } from "next"
import { GetStaticPropsResult } from "next/types"

import type { BreadcrumbSpecialCategoryDocument, PdpDocument, PdpSizesDocument } from "../prismicio/types.generated"

export const buildProductPage = async (
    redisItem: RedisProductItem,
    pageContext: GetStaticPropsContext<{ slug: string[] }>
): Promise<GetStaticPropsResult<(StaticPropsCommon & { page: ProductPage<PrismicPdpJson> }) | NotFoundProps>> => {
    const { deps, locale, ...others } = redisItem
    const product = await retry(() =>
        createClient()
            .product({ id: deps.ct.id, locale1: locale, locale2: locale })
            .then((res) => res.product)
    )
    if (!product || !product.masterData.published) {
        const qs = new URLSearchParams()
        qs.set("redirect", "true")
        const path = `/${pageContext.params.slug.slice(0, -2).filter(typedBoolean).join("/")}?${qs}`
        return { redirect: { destination: path, permanent: false, basePath: false } }
    }
    const previewData = pageContext.previewData

    const prismic = await retry(() =>
        previewData ? prismicClient({ previewData: previewData }).getSingle("pdp") : fetchS3Pdp<PdpDocument>()
    ).then((res) => transPdp(res.data))
    const pdpSizesJson = await retry(() =>
        previewData
            ? prismicClient({ previewData: previewData }).getSingle("pdp_sizes")
            : fetchS3PdpSizes<PdpSizesDocument>()
    ).then((res) => transPdpSizes(res.data))
    const breadcrumbSpecialCategory = await retry(() =>
        previewData
            ? prismicClient({ previewData }).getSingle("breadcrumb_special_category")
            : fetchS3BreadcrumbSpecialCategory<BreadcrumbSpecialCategoryDocument>()
    ).then((res) => transBreadcrumbSpecialCategory(res.data.breadcrumb_special_category))

    return buildPageProps(pageContext, {
        locale,
        ...others,
        productDetailInfo: buildProductDetailInfo(
            { product, prismic, pdpSizesJson, onlineOnly: true },
            locale.toLowerCase()
        ),
        prismic: { ...prismic, sustainable: [] },
        breadcrumbSpecialCategory,
    })
}

export const ProductPageContent = ({ page }: { page: ProductPage<PrismicPdpJson> }) => {
    const list = usePdpBreadcrumb(page.path.split("/"), page.breadcrumbSpecialCategory)
    if (!page.prismic || !page.productDetailInfo || !page.productDetailInfo.published) return <PageNotFound />
    return (
        <Layout gtmTypeOfPage="pdp">
            <PdpProvider key={page.path} pdp={page.prismic} detail={page.productDetailInfo}>
                <PdpBreadcrumbs list={list} />
                <Pdp />
            </PdpProvider>
        </Layout>
    )
}
